import { type UndoBoundary } from './types';

/**
 * 用于识别自然输入停顿点的标点、符号或空白字符。
 */
const PUNCTUATION_OR_SPACE_RE = /[\p{P}\p{S}\s]/u;

/**
 * 根据文本变化推断本次输入应该使用的撤销边界。
 *
 * 普通连续输入返回 `typing`，允许在短时间内合并成一次撤销；插入换行返回 `enter`，
 * 插入内容以标点、符号或空白结尾时返回 `punctuation`，让这些自然停顿点切开新的历史节点。
 * 删除、替换或无法识别为纯插入的变化会退回 `typing`，由调用方决定是否额外传入更明确的边界。
 *
 * @param oldText - 原始文本。
 * @param nextText - 新文本。
 * @returns 撤销边界。
 */
export const getTypingBoundary = (oldText: string, nextText: string): UndoBoundary => {
    if (nextText.length <= oldText.length) return 'typing';

    const minLength = Math.min(oldText.length, nextText.length);

    let prefix = 0;
    while (prefix < minLength && oldText.charCodeAt(prefix) === nextText.charCodeAt(prefix)) prefix++;

    let suffix = 0;
    while (
        suffix < minLength - prefix &&
        oldText.charCodeAt(oldText.length - 1 - suffix) === nextText.charCodeAt(nextText.length - 1 - suffix)
    ) {
        suffix++;
    }

    const inserted = nextText.slice(prefix, nextText.length - suffix);
    if (inserted.includes('\n')) return 'enter';

    return PUNCTUATION_OR_SPACE_RE.test(inserted.slice(-1)) ? 'punctuation' : 'typing';
};
